import { formatDistance } from "date-fns";
import { GitCommitHorizontal, UserPen } from "lucide-react";
import { useUserDisplayNames } from "@/store/teamsStore";

interface CustomSkillSyncBannerProps {
  syncVersion: string | null;
  syncUpdatedBy: string | null;
  updatedAt: Date;
}

/**
 * Compact inline status for a single custom skill, mirroring SopSyncBanner.
 * A skill is "covered by sync" iff syncVersion is non-null (sync leaves a SHA
 * behind that survives later user edits).
 */
export function CustomSkillSyncBanner({
  syncVersion,
  syncUpdatedBy,
  updatedAt,
}: CustomSkillSyncBannerProps) {
  const userNames = useUserDisplayNames();
  const relativeTime = formatDistance(new Date(updatedAt), new Date(), {
    addSuffix: true,
  });

  if (syncUpdatedBy === "sync" && syncVersion) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <GitCommitHorizontal className="h-3.5 w-3.5" />
        <span>
          Configured ·{" "}
          <span className="font-mono">{syncVersion.slice(0, 7)}</span> ·{" "}
          {relativeTime}
        </span>
      </div>
    );
  }

  if (syncUpdatedBy && syncUpdatedBy !== "sync") {
    const userName = userNames.get(syncUpdatedBy) ?? "an admin";
    if (syncVersion !== null) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-warning-foreground">
          <UserPen className="h-3.5 w-3.5" />
          <span>
            Manually edited by {userName} · {relativeTime} · will revert on next
            system update
          </span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <UserPen className="h-3.5 w-3.5" />
        <span>
          Edited by {userName} · {relativeTime}
        </span>
      </div>
    );
  }

  return (
    <div className="text-xs text-muted-foreground">Not yet configured</div>
  );
}
